import { RcIcon } from '@/components/atoms/RcIcon'
import { RcTrans } from '@/components/atoms/RcTrans'
import { RcSelect } from '@/components/molecules/input/RcSelect'
import { RcButton } from '@/components/molecules/interactive/RcButton'
import { MatchCard } from '@/components/pages/Competition/components/match/card/MatchCard'
import { useCompetition } from '@/entity/competition/useCompetition'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { MatchState, WinCondition } from '@rallycry/api-suite-typescript'
import { BracketAssignmentResource } from '@rallycry/api-suite-typescript/dist/models/BracketAssignmentResource'
import { MatchGameResultCommand } from '@rallycry/api-suite-typescript/dist/models/MatchGameResultCommand'
import { Form, Formik } from 'formik'
import { first, isUndefined, last } from 'lodash-es'
import { ChangeEvent, ReactNode, useEffect, useState } from 'react'
import { MatchResultsMachine } from './match-results-machine'
import { MatchResultFlowActions } from './MatchResultFlowActions'

const enum CommandState {
  NORMAL = 0,
  FORCE_SCORES = 1,
  RESET_MATCH = 2,
  SET_TIE = 3,
  SET_LEFT = 4,
  SET_RIGHT = 5,
  SEND_TOGGLE_FORFEIT = 6
}

interface CommandValueParams {
  winner?: number
}

interface CommandValue {
  cmd: string
  params?: CommandValueParams
}

interface CommandOption {
  key: CommandState
  display: ReactNode
  value: CommandValue
}

const isWinner = (
  it: MatchGameResultCommand,
  assignment?: BracketAssignmentResource
) => it.winner === assignment?.id

const getScore = (
  scoreSubmissions?: MatchGameResultCommand[],
  assignment?: BracketAssignmentResource
) => scoreSubmissions?.filter(it => it.winner === assignment?.id).length

export const ReportScores = ({ machine }: { machine: MatchResultsMachine }) => {
  const { isCompetitionModerator } = useCompetition()
  const [state, send] = machine
  const { match, bracketAssignment1, bracketAssignment2 } = state.context
  const isMatchComplete = match?.state === MatchState.COMPLETE
  const options: CommandOption[] = []

  const playerOne = first(match?._expanded?.competitionEntry)?.alternateName
  const playerTwo = last(match?._expanded?.competitionEntry)?.alternateName

  const collection = state.context.scoreSubmissions

  const initialValues = {
    games: collection.map(score => ({
      ordinal: score.ordinal,
      winner: score.winner,
      score1: score.scores?.[0] || 0,
      score2: score.scores?.[1] || 0
    }))
  }

  if (state.can('SEND_TOGGLE_FORFEIT')) {
    options.push({
      key: CommandState.SEND_TOGGLE_FORFEIT,
      display: <RcTrans i18nKey='competition:toggle-forfeit-label' />,
      value: { cmd: 'SEND_TOGGLE_FORFEIT' }
    })
  }

  if (
    state.matches('reportScores') &&
    state.context.isPlayerOrCoach &&
    !state.context.isBothTeams &&
    !isMatchComplete
  ) {
    options.push({
      key: CommandState.NORMAL,
      display: <RcTrans i18nKey='competition:report-scores-dropdown-label' />,
      value: { cmd: 'REPORT_SCORES' }
    })
  }

  if (
    state.matches('reportScores') &&
    (state.context.isAdmin || state.context.isBothTeams) &&
    !isMatchComplete
  ) {
    options.push({
      key: CommandState.FORCE_SCORES,
      display: <RcTrans i18nKey='competition:force-scores-dropdown-label' />,
      value: { cmd: 'FORCE_SCORES' }
    })
  }
  if (state.can('RESET_MATCH')) {
    options.push({
      key: CommandState.RESET_MATCH,
      display: <RcTrans i18nKey='competition:reset-match-dropdown-label' />,
      value: { cmd: 'RESET_MATCH' }
    })
  }
  if (!isMatchComplete) {
    options.push(
      ...[
        {
          key: CommandState.SET_TIE,
          display: (
            <RcTrans i18nKey='competition:force-match-tie-dropdown-label' />
          ),
          value: { cmd: 'SET_TIE' }
        },
        {
          key: CommandState.SET_LEFT,
          display: `Force ${playerOne} as Winner`,
          value: {
            cmd: 'SET_WINNER',
            params: {
              winner: state.context.bracketAssignment1?.id
            }
          }
        },
        {
          key: CommandState.SET_RIGHT,
          display: `Force ${playerTwo} as Winner`,
          value: {
            cmd: 'SET_WINNER',
            params: {
              winner: state.context.bracketAssignment2?.id
            }
          }
        }
      ]
    )
  }
  const [command, setCommand] = useState<CommandState>(
    isCompetitionModerator && isMatchComplete
      ? CommandState.SEND_TOGGLE_FORFEIT
      : (first(options)?.key ?? CommandState.NORMAL)
  )

  const hideForfeit =
    command == CommandState.RESET_MATCH ||
    command === CommandState.SEND_TOGGLE_FORFEIT

  const handleSubmit = (values: typeof initialValues) => {
    const cmd = options?.find(it => it.key?.toString() === command?.toString())

    if (cmd?.value) {
      send(cmd.value?.cmd as any, cmd.value?.params)
      return
    }

    throw Error('no command found for selected option')
  }

  useEffect(() => {
    const scoreReport = document.querySelector('.score-row:last-child')
    scoreReport?.scrollIntoView({ behavior: 'smooth' })
  }, [state.context.scoreSubmissions])

  const scoreCheck = (
    score: MatchGameResultCommand & { isLocked?: boolean },
    assignment?: BracketAssignmentResource
  ) => (
    <RcButton
      disabled={score.isLocked}
      onClick={() => {
        send('ADD_SCORE', {
          ordinal: score.ordinal,
          winner: assignment?.id
        })
      }}
      variant={isWinner(score, assignment) ? 'contained' : 'outlined'}
      fullWidth
    >
      {isWinner(score, assignment) ? (
        <RcIcon icon={['fal', 'check']} size='xs' mr={2} />
      ) : null}{' '}
      <RcTrans i18nKey='competition:match.win' />
    </RcButton>
  )

  const scoreNumeric = (
    score: MatchGameResultCommand & { isLocked?: boolean },
    assignment?: BracketAssignmentResource
  ) => {
    const value =
      (assignment === bracketAssignment1
        ? score.scores?.[0]
        : score.scores?.[1]) || 0
    return (
      <TextField
        disabled={score.isLocked}
        type='number'
        variant='outlined'
        value={value}
        onChange={event => {
          send('ADD_SCORE', {
            ordinal: score.ordinal,
            score1:
              assignment === bracketAssignment1
                ? Number(event.target.value)
                : undefined,
            score2:
              assignment === bracketAssignment2
                ? Number(event.target.value)
                : undefined
          })
        }}
      />
    )
  }

  const handleForfeitToggle = (event: ChangeEvent<HTMLInputElement>) =>
    send('TOGGLE_FORFEIT')

  const score1 = getScore(collection, bracketAssignment1)
  const score2 = getScore(collection, bracketAssignment2)

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue }) => (
        <Form>
          <Stack direction='column' spacing={3}>
            {state.context.bracketSettings?.numericGameScore ? (
              <Typography variant='body2' align='center'>
                Enter your <strong>in-game</strong> scores to{' '}
                <strong>begin </strong>
                finalizing the match
              </Typography>
            ) : null}
            {isCompetitionModerator ? (
              <Paper
                elevation={2}
                sx={{
                  p: 2.5,
                  display: 'flex',
                  direction: 'row',
                  justifyContent: 'flex-start'
                }}
              >
                <Stack direction='column' spacing={4} width='100%'>
                  <Stack
                    display='flex'
                    direction='row'
                    alignItems='center'
                    spacing={2}
                  >
                    <Typography variant='body1' align='center'>
                      <RcTrans i18nKey='competition:moderator-actions-label' />
                    </Typography>
                    <Tooltip
                      title={'These are actions only moderators can take'}
                    >
                      <RcIcon icon={['fal', 'info-circle']} size='lg' />
                    </Tooltip>
                  </Stack>

                  <RcSelect
                    property='command'
                    source={{ command }}
                    update={async (val: { command: CommandState }) =>
                      setCommand(val.command)
                    }
                  >
                    {options.map(it => (
                      <MenuItem key={it.key} value={it.key}>
                        {it.display}
                      </MenuItem>
                    ))}
                  </RcSelect>

                  {hideForfeit ? null : (
                    <Stack direction='row' spacing={2} alignItems='center'>
                      <Checkbox
                        onChange={handleForfeitToggle}
                        checked={state.context.isForfeit}
                      />
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography variant='caption' color='textSecondary'>
                          <RcTrans i18nKey='competition:report-scores-forfeit-desc-label' />
                        </Typography>
                        <Tooltip
                          title={'This completes the match as a forfeit.'}
                        >
                          <RcIcon icon={['fal', 'info-circle']} size='sm' />
                        </Tooltip>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            ) : null}
            {command > 1 || state.context.match?.state === MatchState.COMPLETE
              ? null
              : collection?.map(it => (
                  <Card key={it.ordinal} className='score-row'>
                    <CardContent>
                      <Grid
                        key={it.ordinal}
                        container
                        direction='row'
                        alignItems='flex-end'
                      >
                        <Grid item xs>
                          {state.context.bracketSettings?.numericGameScore
                            ? scoreNumeric(it, bracketAssignment1)
                            : scoreCheck(it, bracketAssignment1)}
                        </Grid>
                        <Grid item xs={6}>
                          <Stack
                            direction='column'
                            spacing={1}
                            alignItems='center'
                          >
                            <Typography variant='subtitle1' align='center'>
                              <RcTrans
                                i18nKey='competition:match.game-number'
                                tOptions={{
                                  ordinal: it.ordinal
                                }}
                              />
                            </Typography>
                            {state.context.bracketSettings?.winCondition ===
                            WinCondition.BEST_OF ? (
                              <Button
                                variant={
                                  isUndefined(it.winner)
                                    ? 'contained'
                                    : 'outlined'
                                }
                                disabled={it.isLocked}
                                onClick={() =>
                                  send('ADD_SCORE', {
                                    ordinal: it.ordinal,
                                    winner: undefined,
                                    score1: undefined,
                                    score2: undefined
                                  })
                                }
                              >
                                <RcTrans i18nKey='competition:match.TIE' />
                              </Button>
                            ) : null}
                          </Stack>
                        </Grid>
                        <Grid item xs style={{ textAlign: 'right' }}>
                          {state.context.bracketSettings?.numericGameScore
                            ? scoreNumeric(it, bracketAssignment2)
                            : scoreCheck(it, bracketAssignment2)}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
            <MatchResultFlowActions
              machine={machine}
              title={<RcTrans i18nKey='competition:report-scores' />}
              topContent={
                <Box sx={{ p: 7 }}>
                  <MatchCard
                    match={match}
                    competition={state.context.competition}
                    score1={score1}
                    score2={score2}
                    hideViewMatch
                  />
                </Box>
              }
            />
            <RcButton type='submit' variant='contained'>
              Submit Results
            </RcButton>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}
